<script setup lang="ts">
import { computed } from 'vue';

import { useBookingQuoteStore } from '@/entities/quickQuote/lib/booking-store';
import { formatMoney } from '@/shared/helpers/formatters';

import BookingCard from './BookingCard.vue';

import type { EstimateCost } from '@/entities/quickQuote/lib/booking-api.types';

type SummaryLineItem = {
	id: string;
	label: string;
	note?: string;
	cost: EstimateCost;
};

const bookingStore = useBookingQuoteStore();

const formatChipCount = (count: number) =>
	`${count} ${count === 1 ? 'chip' : 'chips'}`;

const vehicle = computed(() => bookingStore.selectedVehicle);
const lineItems = computed<SummaryLineItem[]>(() => {
	const glass = bookingStore.selectedGlass;
	return [
		...(glass
			? [
					{
						id: `glass:${glass.key}`,
						label: glass.label,
						note:
							bookingStore.selectedGlassOption === 'repair'
								? formatChipCount(bookingStore.chipCount)
								: undefined,
						cost: bookingStore.glassCost
					}
				]
			: []),
		...bookingStore.requiredOptions.map(option => ({
			id: `required:${option.key}`,
			label: option.label,
			cost: bookingStore.getOptionCost(option)
		})),
		...bookingStore.selectedExtraOptions.map(option => ({
			id: `extra:${option.key}`,
			label: option.label,
			cost: bookingStore.getOptionCost(option)
		}))
	];
});
const total = computed(() => bookingStore.quoteTotal);
</script>

<template>
	<BookingCard class="tw3-p-5">
		<h3
			class="tw3-mb-4 tw3-text-xs tw3-font-bold tw3-uppercase tw3-tracking-wider tw3-text-surface-500"
		>
			Quote summary
		</h3>

		<div
			v-if="vehicle"
			class="tw3-flex tw3-gap-3 tw3-border-b tw3-border-surface-200 tw3-pb-4"
		>
			<i class="pi pi-car tw3-mt-0.5 tw3-text-primary-600" />
			<div>
				<div class="tw3-font-semibold">
					{{ vehicle.year }}
					{{ vehicle.make }}
					{{ vehicle.model }}
				</div>
				<div
					v-if="bookingStore.vehicleVin"
					class="tw3-mt-0.5 tw3-text-xs tw3-tracking-wider tw3-text-surface-400"
				>
					VIN {{ bookingStore.vehicleVin }}
				</div>
			</div>
		</div>

		<ul
			v-if="lineItems.length"
			class="tw3-m-0 tw3-mt-4 tw3-grid tw3-list-none tw3-gap-2.5 tw3-p-0"
		>
			<li
				v-for="item in lineItems"
				:key="item.id"
				class="tw3-flex tw3-items-start tw3-justify-between tw3-gap-3 tw3-text-sm tw3-text-surface-700"
			>
				<span>
					{{ item.label }}
					<span
						v-if="item.note"
						class="tw3-mt-0.5 tw3-block tw3-text-xs tw3-text-surface-400"
					>
						{{ item.note }}
					</span>
				</span>
				<span>{{ formatMoney(item.cost.total) }}</span>
			</li>
		</ul>

		<div
			v-if="total !== null"
			class="tw3-mt-4 tw3-flex tw3-items-center tw3-justify-between tw3-border-t tw3-border-surface-200 tw3-pt-4 tw3-font-semibold"
		>
			<span>Total</span>
			<span class="tw3-text-2xl tw3-font-extrabold tw3-text-primary-600">
				{{ formatMoney(total) }}
			</span>
		</div>
		<p class="tw3-mt-1 tw3-text-right tw3-text-[0.7rem] tw3-text-surface-400">
			Tax included
		</p>
		<div
			v-if="total !== null && bookingStore.paymentRequired"
			class="-tw3-mx-3 tw3-mt-3 tw3-flex tw3-items-center tw3-justify-between tw3-gap-3 tw3-rounded-xl tw3-bg-primary-50 tw3-px-3 tw3-py-2.5 tw3-font-semibold tw3-text-primary-700"
		>
			<span>Due today {{ bookingStore.dueTodayPercent }}</span>
			<span class="tw3-text-lg tw3-font-extrabold">
				{{ formatMoney(bookingStore.prepaymentAmount || 0) }}
			</span>
		</div>
	</BookingCard>
</template>
